import { Context } from "urql";
import Cookies from "js-cookie";
import qs from "query-string";

import { RedirectionError } from "@lululemon/mwa-utils";
import { constructGraphQlQuery, getPdpGQLProductParams } from "../utils/common";
import { getCNEGraphqlEndpoint } from "./helpers";
import { convertLocaleForGraphql } from "@lululemon/ecom-common/src/util/helpers";
import { constructPDPFields } from "./fetchPDP";
import { getProductDetailPage } from "./graphql-component-queries";
import TransformProductDataToLegacy from "../etl/gql-to-legacy-pdp";
import { Logger } from "../utils/logger";
import { getDataUrl } from "../utils/url";
import { GQL_RESPONSE_EMPTY } from "../constants/loadDataRoutingConstants";

const handlePDPResponse = async (response) => {
  if (!response?.data?.productDetailPage) {
    const responseStatus = response?.status || 404; // fallback to missing page
    if (responseStatus === 404) {
      throw new Error(`Failed to fetch PDP response`, {
        cause: { message: GQL_RESPONSE_EMPTY, code: 404 },
      });
    } else {
      throw new RedirectionError({
        message: `Failed to fetch PDP ${response.data}`,
        url: `/error/${responseStatus}?component=product-detail-page`,
      });
    }
  }
  const productDetailPageData = response.data.productDetailPage || {};
  // Legacy data consumers, DEPRECATED
  const data = TransformProductDataToLegacy(productDetailPageData);

  // CE2-3519: Markdown consolidation ABT:
  // MT returns data for eligible products in this field `markdownConsolidated`.
  // This needs to be legacy-transformed too and passed along regular data.
  // When needed, "markdown consolidated" data is used instead.
  const legacyMarkdownConsolidatedData =
    productDetailPageData?.markdownConsolidated
      ? TransformProductDataToLegacy(productDetailPageData.markdownConsolidated)
      : null;

  return {
    data,
    legacyMarkdownConsolidatedData,
    productDetailPageData,
    raw: { headers: response.headers },
  };
};

/**
 * Function to extract graphql product params from product page url
 * @param {string} path - product page url without the hostname
 * @param {string} queryString - querystring from product page url
 * @param {object} req - request object available in SSR
 * @param {string} locale - International locale string from page context - Regional Accuracy; prefer nextLocale
 * @returns {object|null} - parameters for fetching legacy pdp from graphql
 */
export function getPdpGraphqlParams(path, locale, queryString, req = null) {
  const dataUrl = getDataUrl(path, queryString, locale);
  const parsedDataUrl = qs.parseUrl(dataUrl);
  const queryParams = qs.stringify(parsedDataUrl.query, {
    skipNull: true,
    sort: false,
  });
  const fetchUrl = queryParams
    ? `${parsedDataUrl.url}?${queryParams}`
    : dataUrl;

  const gqlProductParams = getPdpGQLProductParams(fetchUrl, queryString);
  const sl =
    req?.query?.sl ||
    req?.cookies?.sl ||
    Cookies.get("sl") ||
    gqlProductParams?.sl;

  // this is no longer true???? mar 2024
  // Regional Accuracy - Phase 1 - use Locale from context and replace the - with _
  // const usrLocale = locale.replace("-", "_");

  return {
    ...gqlProductParams,
    sl,
    locale: convertLocaleForGraphql(locale),
  };
}

/**
 * Function to retrieve productData using legacy pdp response.
 * @param {object} params
 * @params {string} params.productId
 * @params {string} params.unifiedID
 * @params {string} params.category
 * @param graphqlClient
 * @param {boolean} shouldFetchPCMData - flag to determine if PCM graphql endpoint is called - Footwear
 * @param {object} ldFlags - LD flags.
 * @returns {*}
 */
export const fetchPDPResponse = async (
  params = {},
  graphqlClient,
  shouldFetchPCMData = false,
  ldFlags = {}
) => {
  try {
    Context.url = getCNEGraphqlEndpoint();

    // Needs id in query
    const { query: pdpGraphQuery, options: queryOption } =
      constructGraphQlQuery({
        operation: "productDetailPage",
        fields: constructPDPFields(params),
        returns: getProductDetailPage({
          fetchPCMMedia: shouldFetchPCMData,
          fetchMarkdownConsolidated: ldFlags.pdpPriceConsolidation || false,
        }),
      });

    const graphQuery = `query GetPdpDataById($id: String!, $category: String!, $unifiedId: String!, $locale: String, $forceMemberCheck: Boolean, $sl: String, $forcePcm: Boolean) {
        ${pdpGraphQuery}
      }
    `;

    const response = await graphqlClient
      .query(graphQuery, queryOption, Context)
      .toPromise();
    return handlePDPResponse(response);
  } catch (error) {
    Logger.error(new Error(`Error fetching PDP response`), {
      cause: error,
      functionName: "fetchPDPResponse",
      productId: params?.productId,
    });
    throw error;
  }
};

export default fetchPDPResponse;
